import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaEye, FaEyeSlash, FaCamera } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../App.css';
import client from '../api/client';
import { toast } from 'react-toastify';

function ProfessionalRegister() {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    profession: '',
    name: '',
    address: '',
    gender: '',
    dateOfBirth: '',
    pincode: '',
    qualification: '',
    specialization: '',
    registrationNumber: '',
    experience: '',
    experienceDetails: '',
    mobileNumber: '',
    countryNumber: '',
    emailId: '',
    password: '',
    reenterPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showReenterPassword, setShowReenterPassword] = useState(false);
  const [professions, setProfessions] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [qualificationOptions, setQualificationOptions] = useState([]);
  const [isQualificationDisabled, setIsQualificationDisabled] = useState(true);
  const [location, setLocation] = useState('');
  const [idDistrict, setIdDistrict] = useState(null);
  const [pincodeError, setPincodeError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [contactError, setContactError] = useState('');
  const [isValidatingPincode, setIsValidatingPincode] = useState(false);
  const [isValidatingContact, setIsValidatingContact] = useState(false);
  const [isPincodeValid, setIsPincodeValid] = useState(false);
  const [isContactValid, setIsContactValid] = useState(false);
  const [isMobileValid, setIsMobileValid] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [registrationCertificateFile, setRegistrationCertificateFile] = useState(null);

  // Fetch profession types and degrees
  useEffect(() => {
    client.get('/api/professional-types')
      .then((response) => {
        const list = response.data?.data || [];
        setProfessions(list);

        // Default to Doctor if present and nothing selected
        if (!formData.profession) {
          const doctor = list.find(
            (p) => p.professionTypeName?.toLowerCase() === 'doctor',
          );
          if (doctor) {
            setFormData((prev) => ({ ...prev, profession: String(doctor.idProfessionType) }));
          }
        }
      })
      .catch(() => {
        setProfessions([]);
      });

    client.get('/api/professional-degrees')
      .then((response) => {
        const list = response.data?.data || [];
        setDegrees(list);
      })
      .catch(() => {
        setDegrees([]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update qualification options when profession changes
  useEffect(() => {
    const professionId = parseInt(formData.profession, 10);
    if (Number.isFinite(professionId)) {
      const filtered = degrees
        .filter((deg) => deg.idProfessional === professionId)
        .map((deg) => deg.professionalDegreeName)
        .filter(Boolean);
      setQualificationOptions(filtered);
      setIsQualificationDisabled(filtered.length === 0);
      if (filtered.length === 0) {
        setFormData((prev) => ({ ...prev, qualification: '' }));
      }
    } else {
      setQualificationOptions([]);
      setIsQualificationDisabled(true);
      setFormData((prev) => ({ ...prev, qualification: '' }));
    }
  }, [degrees, formData.profession]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validation for pincode (only numbers, max 6 digits)
    if (name === 'pincode') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6);
      setFormData(prev => ({ ...prev, [name]: numericValue }));
      setPincodeError('');
      setIsPincodeValid(false);
      setLocation('');
      setIdDistrict(null);
      return;
    }
    if (name === "password" || name === "reenterPassword") {

      if (value.length > 20) {
        toast.error("Password cannot exceed 20 characters!");
        return;
      }
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }

    if (name === 'mobileNumber') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData((prev) => ({ ...prev, [name]: numericValue }));
      setMobileError('');
      setIsMobileValid(false);
      setContactError('');
      setIsContactValid(false);
      return;
    }

    if (name === 'emailId') {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setEmailError('');
      setIsEmailValid(false);
      setContactError('');
      setIsContactValid(false);
      return;
    }

    // Validation for mobile number (only numbers, max 10 digits)
    if (name === 'mobileNumber') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: numericValue }));
      setContactError('');
      setIsContactValid(false);
      return;
    }

    // Validation for experience (0-70)
    if (name === 'experience') {
      let numValue = parseInt(value);
      if (isNaN(numValue) || numValue < 0) {
        setFormData(prev => ({ ...prev, [name]: '' }));
        return;
      }
      if (numValue > 70) {
        numValue = 70;
      }
      setFormData(prev => ({ ...prev, [name]: numValue.toString() }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle rich text editor changes
  const handleExperienceDetailsChange = (value) => {
    // Strip HTML tags to check character count
    const textContent = value.replace(/<[^>]*>/g, '');
    if (textContent.length <= 8000) {
      setFormData(prev => ({ ...prev, experienceDetails: value }));
    }
  };

  // Handle file uploads
  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (!file) return;

    // File size validation (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error(`${fileType} file size must be less than 5MB!`);
      e.target.value = ''; // Reset file input
      return;
    }

    // File type validation
    let allowedTypes = [];
    if (fileType === 'CV') {
      allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('CV must be a PDF, Word document, or image (.pdf, .doc, .docx, .jpg, .jpeg, .png)!');
        e.target.value = '';
        return;
      }
    } else if (fileType === 'Photo') {
      allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Photo must be a JPEG or PNG image (.jpg, .jpeg, .png)!');
        e.target.value = '';
        return;
      }
    } else if (fileType === 'Registration Certificate') {
      allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Registration Certificate must be a PDF, Word document, or image (.pdf, .doc, .docx, .jpg, .jpeg, .png)!');
        e.target.value = '';
        return;
      }
    }

    // Set the file
    if (fileType === 'CV') {
      setCvFile(file);
    } else if (fileType === 'Photo') {
      setPhotoFile(file);
      // Create preview URL for photo
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else if (fileType === 'Registration Certificate') {
      setRegistrationCertificateFile(file);
    }
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Character counters
  const addressCount = formData.address.length;
  // Strip HTML tags for character count
  const experienceCount = formData.experienceDetails.replace(/<[^>]*>/g, '').length;

  // Validate pincode against API
  const validatePincode = async () => {
    let message = '';
    setPincodeError('');
    setIsPincodeValid(false);
    setLocation('');
    setIdDistrict(null);

    if (formData.pincode.length !== 6) {
      message = 'Enter a 6-digit pincode.';
      setPincodeError(message);
      return { ok: false, message };
    }

    setIsValidatingPincode(true);
    try {
      const response = await client.get(`/api/districts/validate-pincode?pincode=${formData.pincode}`);
      const result = response.data?.data;

      if (result?.districtName) {
        setLocation(result.districtName);
        setIdDistrict(result.idDistrict ?? null);
        setIsPincodeValid(true);
        return { ok: true, message: '' };
      }

      message = 'Enter a valid pincode.';
      setPincodeError(message);
      setIsPincodeValid(false);
      return { ok: false, message };
    } catch (error) {
      message = 'Unable to validate pincode. Please try again.';
      setPincodeError(message);
      setIsPincodeValid(false);
      return { ok: false, message };
    } finally {
      setIsValidatingPincode(false);
    }
  };

  // Validate email/mobile against API (field can be 'mobile', 'email', or 'both')
  const validateContact = async (field = 'both') => {
    let message = '';
    if (field === 'both') {
      setContactError('');
    }
    setIsContactValid(false);
    if (field !== 'mobile') {
      setEmailError('');
      setIsEmailValid(false);
    }
    if (field !== 'email') {
      setMobileError('');
      setIsMobileValid(false);
    }

    const emailValid = field === 'mobile' ? true : /\S+@\S+\.\S+/.test(formData.emailId);
    const mobileValid = field === 'email' ? true : formData.mobileNumber.length === 10;

    if (!emailValid || !mobileValid) {
      if (!emailValid) {
        setEmailError('Enter a valid email.');
        message = 'Enter a valid email.';
      }
      if (!mobileValid) {
        setMobileError('Enter a valid 10-digit mobile number.');
        message = message || 'Enter a valid 10-digit mobile number.';
      }
      if (field === 'both' && message) {
        setContactError(message);
      }
      return { ok: false, message: message || 'Enter valid contact details.' };
    }

    setIsValidatingContact(true);
    try {
      const payload = {
        emailID: field === 'mobile' ? null : formData.emailId || null,
        mobileNumber: field === 'email' ? null : formData.mobileNumber || null
      };
      const response = await client.post('/api/users/validate-contact', payload);
      const exists = response.data?.data;

      if (exists) {
        if (field === 'email') {
          message = 'Email already exists. Enter a different one.';
          setEmailError(message);
        } else if (field === 'mobile') {
          message = 'Mobile number already exists. Enter a different one.';
          setMobileError(message);
        } else {
          message = 'Email or mobile already exists. Enter a different one.';
          setContactError(message);
        }
        return { ok: false, message };
      }

      if (field === 'email') {
        setIsEmailValid(true);
      } else if (field === 'mobile') {
        setIsMobileValid(true);
      } else {
        setIsEmailValid(true);
        setIsMobileValid(true);
        setIsContactValid(true);
      }
      return { ok: true, message: '' };
    } catch (error) {
      message = 'Unable to validate contact. Please try again.';
      if (field === 'email') {
        setEmailError(message);
      } else if (field === 'mobile') {
        setMobileError(message);
      } else {
        setContactError(message);
      }
      return { ok: false, message };
    } finally {
      setIsValidatingContact(false);
    }
  };

  // Password validation
  const passwordsMatch = formData.password === formData.reenterPassword && formData.reenterPassword !== '';

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    const [pincodeResult, mobileResult, emailResult] = await Promise.all([
      validatePincode(),
      validateContact('mobile'),
      validateContact('email')
    ]);

    if (!pincodeResult.ok) {
      toast.error(pincodeResult.message || 'Please enter a valid pincode.');
      return;
    }
    if (!mobileResult.ok) {
      toast.error(mobileResult.message || 'Please enter a valid mobile number.');
      return;
    }
    if (!emailResult.ok) {
      toast.error(emailResult.message || 'Please enter a valid email ID.');
      return;
    }

    // Validate password match
    if (formData.password !== formData.reenterPassword) {
      toast.error('Passwords do not match!');
      return;
    }

    // Validate password length
    if (formData.password.length < 6 || formData.password.length > 20) {
      toast.error('Password must be between 6 and 20 characters!');
      return;
    }

    // Validate experience range
    const experience = parseInt(formData.experience);
    if (isNaN(experience) || experience < 0 || experience > 70) {
      toast.error('Experience must be between 0 and 70 years!');
      return;
    }

    // Validate experience details character count (strip HTML tags)
    const experienceDetailsText = formData.experienceDetails.replace(/<[^>]*>/g, '');
    if (experienceDetailsText.length > 8000) {
      toast.error('Experience details must not exceed 8000 characters!');
      return;
    }
    if (experienceDetailsText.trim().length === 0) {
      toast.error('Experience details is required!');
      return;
    }

    // Validate file uploads
    if (!cvFile) {
      toast.error('Please upload your CV!');
      return;
    }
    if (!photoFile) {
      toast.error('Please upload your photo!');
      return;
    }
    if (!registrationCertificateFile) {
      toast.error('Please upload your registration certificate!');
      return;
    }

    if (!location) {
      toast.error('Enter a valid pincode to fetch location.');
      return;
    }

    // Prepare payload for API
    const degreeMatch = degrees.find(
      (deg) =>
        deg.professionalDegreeName === formData.qualification &&
        deg.idProfessional === parseInt(formData.profession, 10)
    );
    const idProfessionalDegree = degreeMatch?.idProfessionalDegree ?? null;

    const payload = new FormData();
    payload.append('EmailID', formData.emailId);
    payload.append('MobileNumber', formData.mobileNumber);
    payload.append('CountryNumber', formData.countryNumber);
    payload.append('Password', formData.password);
    payload.append('IdProfessionType', formData.profession);
    payload.append('ProfessionalName', formData.name);
    if (idProfessionalDegree) payload.append('IdProfessionalDegree', idProfessionalDegree);
    payload.append('Address', formData.address);
    payload.append('IdDistrict', idDistrict ?? 0);
    payload.append('Pincode', formData.pincode);
    payload.append('PincodeName', location);
    payload.append('Specialization', formData.specialization);
    payload.append('RegistrationNumber', formData.registrationNumber);
    payload.append('DateOfBirth', formData.dateOfBirth);
    payload.append('Gender', formData.gender);
    payload.append('YearsOfExperience', formData.experience);
    payload.append('ExperienceDetails', formData.experienceDetails);
    payload.append('IsSuspended', false);
    payload.append('BioDataFile', cvFile);
    payload.append('PhotoFile', photoFile);
    payload.append('RegistrationCertificateFile', registrationCertificateFile);

    try {
      setIsSubmitting(true);
      await client.post('/api/healthcare-professionals', payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Registration successful!');
      navigate('/');
    } catch (error) {
      toast.error('Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All entered data will be lost.')) {
      setFormData({
        profession: '',
        name: '',
        address: '',
        gender: '',
        dateOfBirth: '',
        pincode: '',
        qualification: '',
        specialization: '',
        registrationNumber: '',
        experience: '',
        experienceDetails: '',
        mobileNumber: '',
        emailId: '',
        password: '',
        reenterPassword: ''
      });
      setCvFile(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      setRegistrationCertificateFile(null);
      navigate('/');
    }
  };

  return (
    <div className="App">
      <Header />

      <section className="login-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-12">
              <div className="login-card">
                <div className="login-header text-center mb-4">
                  <h2 className="login-title">Professional Registration</h2>
                  <p className="login-subtitle">
                    Please fill in all the required details to complete your registration.
                  </p>
                </div>

                <form className="login-form" onSubmit={handleSubmit}>
                  <div className="row">
                    {/* Your Profession */}

                    {/* Photo Upload */}
                    <div className="col-md-12 p-2">

                      <div className="photo-upload-container">
                        <div className="photo-preview-wrapper">
                          {photoPreview ? (
                            <img
                              src={photoPreview}
                              alt="Profile preview"
                              className="photo-preview-circle"
                            />
                          ) : (
                            <div className="photo-placeholder-circle">
                              <FaCamera className="photo-placeholder-icon" />
                            </div>
                          )}
                          <label htmlFor="photoFile" className="photo-upload-overlay">
                            <FaCamera className="camera-icon" />
                          </label>
                          <input
                            type="file"
                            className="photo-file-input"
                            id="photoFile"
                            name="photoFile"
                            accept=".jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange(e, 'Photo')}
                            required
                          />
                        </div>
                        {photoFile && (
                          <small className="form-text text-success d-block mt-2 text-center">
                            ✓ {photoFile.name} ({formatFileSize(photoFile.size)})
                          </small>
                        )}
                        <small className="form-text text-muted d-block mt-1 text-center">
                          JPG, JPEG, PNG (Max 5MB)
                        </small>
                      </div>
                    </div>

                    <div className="col-md-6 p-2">
                      <label htmlFor="profession" className="form-label">
                        Your Profession <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="profession"
                        name="profession"
                        value={formData.profession}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Profession</option>
                        {professions.map((p) => (
                          <option key={p.idProfessionType} value={p.idProfessionType}>
                            {p.professionTypeName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Name */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="name" className="form-label">
                        Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        maxLength="60"
                        required
                      />
                    </div>



                    {/* Gender */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="gender" className="form-label">
                        Gender <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="gender"
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    {/* Date of Birth */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="dateOfBirth" className="form-label">
                        Date of Birth <span className="text-danger">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        id="dateOfBirth"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        max={new Date().toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    {/* Address */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="address" className="form-label">
                        Address <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        id="address"
                        name="address"
                        rows="3"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Enter your address"
                        maxLength="200"
                        required
                      ></textarea>
                      <small className="form-text text-muted">
                        <span id="addressCount">{addressCount}</span>/200 characters
                      </small>
                    </div>

                    {/* Pincode */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="pincode" className="form-label">
                        Pincode <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control w-25"
                          id="pincode"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          onBlur={validatePincode}
                          placeholder="pincode"
                          maxLength="6"
                          pattern="[0-9]{6}"
                          required

                        />
                        <input
                          type="text"
                          className="form-control w-75 form-control-display-only"
                          id="location"
                          name="location"
                          value={location}
                          placeholder="Location"
                          readOnly
                          tabIndex={-1}
                          aria-readonly="true"
                        />

                      </div>
                      {pincodeError && (
                        <small className="form-text text-danger">{pincodeError}</small>
                      )}
                      {isValidatingPincode && (
                        <small className="form-text text-muted">Validating pincode...</small>
                      )}

                    </div>

                    {/* Qualification */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="qualification" className="form-label">
                        Qualification <span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-select"
                        id="qualification"
                        name="qualification"
                        value={formData.qualification}
                        onChange={handleChange}
                        required
                        disabled={isQualificationDisabled}
                      >
                        <option value="">Select Qualification</option>
                        {qualificationOptions.map((qual, index) => (
                          <option key={index} value={qual}>
                            {qual}
                          </option>
                        ))}
                      </select>
                      {isQualificationDisabled && (
                        <small className="form-text text-muted">Please select your profession to load qualifications</small>
                      )}
                    </div>

                    {/* Specialization */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="specialization" className="form-label">
                        Specialization
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="specialization"
                        name="specialization"
                        value={formData.specialization}
                        onChange={handleChange}
                        placeholder="Enter your specialization"
                        maxLength="100"
                      />
                    </div>

                    {/* Registration Number */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="registrationNumber" className="form-label">
                        Registration Number
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="registrationNumber"
                        name="registrationNumber"
                        value={formData.registrationNumber}
                        onChange={handleChange}
                        placeholder="Enter registration number"
                        maxLength="30"
                      />
                    </div>



                    {/* CV Upload */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="cvFile" className="form-label">
                        CV (Resume) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        id="cvFile"
                        name="cvFile"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(e, 'CV')}
                        required
                      />
                      {cvFile && (
                        <small className="form-text text-success d-block mt-1">
                          ✓ Selected: {cvFile.name} ({formatFileSize(cvFile.size)})
                        </small>
                      )}
                      <small className="form-text text-muted">
                        Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG (Max 5MB)
                      </small>
                    </div>

                    {/* Registration Certificate Upload */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="registrationCertificateFile" className="form-label">
                        Registration Certificate <span className="text-danger">*</span>
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        id="registrationCertificateFile"
                        name="registrationCertificateFile"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileChange(e, 'Registration Certificate')}
                        required
                      />
                      {registrationCertificateFile && (
                        <small className="form-text text-success d-block mt-1">
                          ✓ Selected: {registrationCertificateFile.name} ({formatFileSize(registrationCertificateFile.size)})
                        </small>
                      )}
                      <small className="form-text text-muted">
                        Accepted formats: PDF, DOC, DOCX, JPG, JPEG, PNG (Max 5MB)
                      </small>
                    </div>

                    {/* Experience (In Years) */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="experience" className="form-label">
                        Experience (In Years) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="experience"
                        name="experience"
                        value={formData.experience}
                        onChange={handleChange}
                        placeholder="Enter years of experience"
                        min="0"
                        max="70"
                        required
                      />
                    </div>

                    {/* Experience Details */}
                    <div className="col-md-12 p-2">
                      <label htmlFor="experienceDetails" className="form-label">
                        Experience Details <span className="text-danger">*</span>
                      </label>
                      <ReactQuill
                        theme="snow"
                        value={formData.experienceDetails}
                        onChange={handleExperienceDetailsChange}
                        placeholder="Enter your experience details"
                        className="experience-details-editor"
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                            [{ 'indent': '-1' }, { 'indent': '+1' }],
                            ['link'],
                            ['clean']
                          ]
                        }}
                        formats={[
                          'header',
                          'bold', 'italic', 'underline', 'strike',
                          'list', 'bullet', 'indent',
                          'link'
                        ]}
                        style={{
                          minHeight: '150px',
                          marginBottom: '10px'
                        }}
                      />
                      <small className="form-text text-muted">
                        <span id="experienceCount">{experienceCount}</span>/8000 characters
                      </small>
                    </div>

                    {/* Mobile Number */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="mobileNumber" className="form-label">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        {/* Small Country Code */}
                        <input
                          type="text"
                          className="form-control"
                          style={{ maxWidth: "90px" }}
                          id="countryCode"
                          name="countryNumber"
                          value={formData.countryNumber}
                          maxLength={5}
                          onChange={handleChange}
                          placeholder="+91"
                          required
                        />

                        {/* Mobile */}
                        <input
                          type="tel"
                          className="form-control"
                          id="mobileNumber"
                          name="mobileNumber"
                          value={formData.mobileNumber}
                          onChange={handleChange}
                          onBlur={() => validateContact('mobile')}
                          placeholder="Enter 10-digit mobile number"
                          maxLength="10"
                          pattern="[0-9]{10}"
                          required
                        />
                      </div>
                      {mobileError && (
                        <small className="form-text text-danger">{mobileError}</small>
                      )}
                    </div>

                    {/* Email ID */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="emailId" className="form-label">
                        Email ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="emailId"
                        name="emailId"
                        value={formData.emailId}
                        onChange={handleChange}
                        onBlur={() => validateContact('email')}
                        placeholder="Enter your email ID"
                        maxLength="50"
                        required
                      />
                      {emailError && (
                        <small className="form-text text-danger">{emailError}</small>
                      )}
                    </div>
                    {contactError && (
                      <small className="form-text text-danger">{contactError}</small>
                    )}
                    {isValidatingContact && (
                      <small className="form-text text-muted">Validating contact...</small>
                    )}

                    {/* Password */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="password" className="form-label">
                        Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          className="form-control"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Enter password (6-20 characters)"
                          minLength="6"
                          required
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      <small className="form-text text-muted">Password must be 6-20 characters long</small>
                    </div>

                    {/* Re-enter Password */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="reenterPassword" className="form-label">
                        Re-enter Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type={showReenterPassword ? 'text' : 'password'}
                          className={`form-control ${formData.reenterPassword && !passwordsMatch ? 'is-invalid' : formData.reenterPassword && passwordsMatch ? 'is-valid' : ''}`}
                          id="reenterPassword"
                          name="reenterPassword"
                          value={formData.reenterPassword}
                          onChange={handleChange}
                          placeholder="Re-enter password"
                          minLength="6"
                          required
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setShowReenterPassword(!showReenterPassword)}
                        >
                          {showReenterPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      {formData.reenterPassword && !passwordsMatch && (
                        <div className="invalid-feedback d-block">Passwords do not match</div>
                      )}
                    </div>

                    {/* Submit Button */}
                    <div className="text-center mt-4">
                      <button type="submit" className="btn login-submit-btn px-5 me-2" disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Register'}
                      </button>
                      <button
                        type="button"
                        className="btn cancel-btn px-5"
                        onClick={handleCancel}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default ProfessionalRegister;

